/**
 * Life4Earth V2 — Impact Activity Catalog API adapter (DEV-1480, migration 278).
 *
 * Fetches the ENABLED activities: the acts that actually earn credit right now.
 *
 * WHY THIS EXISTS: DimensionRadar used to carry hardcoded "how to grow it" copy
 * that had no connection to the emission engine — it suggested activities that
 * emitted nothing. This endpoint and the engine read the SAME table
 * (impact_activities), so a prompt can never advertise credit the engine won't
 * give. Do not reintroduce hardcoded activity lists in the radar.
 *
 * Disabled activities are ABSENT from this payload — hidden, never greyed.
 * Curation is not a member-facing concept.
 */

import { apiFetch } from "./client";
import i18n from "../i18n/config";

export type DimKey = "mind" | "voice" | "body" | "heart" | "wings" | "roots";
export type Rung = "explorer" | "contributor" | "champion" | "governor";

/** Rung ordering — 'champion' (not 'leader') per Skip's ruling 2026-07-13. */
export const RUNG_ORDER: Rung[] = ["explorer", "contributor", "champion", "governor"];

export interface ImpactActivity {
  activityKey: string;
  dimension: DimKey;
  rung: Rung;
  displayTitle: string;
  displayHint: string | null;
  /** Per-locale overrides: {"fr": {"displayTitle": "...", "displayHint": "..."}} */
  translations: string;
}

/**
 * Resolve a member-facing string with the CLAUDE.md three-tier fallback:
 *   DB translation (translations JSONB) → static locale JSON → English column.
 *
 * The catalog rows are admin-authored (like the project_taxonomy labels), so
 * their translations live in the DB, not in the static locale files. Same
 * pattern as projectStandardOptions.ts / OnboardingCopy.
 */
export function localizedActivity(
  a: ImpactActivity,
  field: "displayTitle" | "displayHint",
): string {
  const lang = (i18n.language || "en").split("-")[0];
  if (lang !== "en" && a.translations) {
    try {
      const map = JSON.parse(a.translations) as Record<string, Record<string, string>>;
      const v = map?.[lang]?.[field];
      if (v && v.trim()) return v;
    } catch {
      /* malformed translations JSONB must never break the radar */
    }
  }
  return (field === "displayTitle" ? a.displayTitle : a.displayHint) || "";
}

/** Enabled activities only. Everything here is guaranteed to emit. */
export async function fetchEnabledActivities(): Promise<ImpactActivity[]> {
  return apiFetch<ImpactActivity[]>(`/api/impact-activities`);
}

/**
 * DEV-1489 Slice C — an activity as seen through the viewer's rung.
 *
 * `locked` acts are the NEXT rung up: shown, but "disable-and-explain" — a tease
 * of what's immediately ahead, never a promise the viewer can act on yet. Acts
 * more than one rung above are omitted entirely (not the immediate horizon).
 */
export interface GatedActivity {
  activity: ImpactActivity;
  locked: boolean;
  /** The rung at which a locked act unlocks. Null when live. */
  unlocksAt: Rung | null;
}

/**
 * Gate a dimension's activities by the viewer's rung:
 *   at/below the viewer's rung → LIVE
 *   exactly one rung above      → LOCKED (teased, with the unlocking rung)
 *   two+ rungs above            → HIDDEN
 *
 * `viewerRung === undefined` disables gating entirely — everything is live. This
 * is the default for any mount that does not opt in (profile cards, fingerprints,
 * etc.), so Slice C changes nothing for them; only the surfaces that pass a rung
 * (the member's own Impact home) actually gate.
 */
export function gateActivitiesByRung(
  activities: ImpactActivity[],
  viewerRung: Rung | undefined,
): GatedActivity[] {
  if (!viewerRung) {
    return activities.map((a) => ({ activity: a, locked: false, unlocksAt: null }));
  }
  const vi = RUNG_ORDER.indexOf(viewerRung);
  const out: GatedActivity[] = [];
  for (const a of activities) {
    const ai = RUNG_ORDER.indexOf(a.rung);
    if (ai <= vi) out.push({ activity: a, locked: false, unlocksAt: null });
    else if (ai === vi + 1) out.push({ activity: a, locked: true, unlocksAt: a.rung });
    // ai > vi + 1 → beyond the immediate horizon; omitted.
  }
  return out;
}

/** Group enabled activities by dimension. A dimension may legitimately be EMPTY. */
export function groupByDimension(
  activities: ImpactActivity[],
): Record<DimKey, ImpactActivity[]> {
  const out: Record<DimKey, ImpactActivity[]> = {
    mind: [], voice: [], body: [], heart: [], wings: [], roots: [],
  };
  for (const a of activities) {
    if (out[a.dimension]) out[a.dimension].push(a);
  }
  // Within a dimension, show the earliest rung first — the next thing a member
  // could plausibly do, not the most advanced.
  for (const d of Object.keys(out) as DimKey[]) {
    out[d].sort((x, y) => RUNG_ORDER.indexOf(x.rung) - RUNG_ORDER.indexOf(y.rung));
  }
  return out;
}
